import React, { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { privateAxios } from "../utils/axios";
import AddServiceRequest from "../components/AddServiceRequest";
import AddServiceParts from "../components/AddServiceParts";
import ServiceRequestInvoice from "../components/Report-Print/ServiceRequest/ServiceRequestInvoice";
import toast from "react-hot-toast";

const TABS = [
  { key: "requested", label: "Requested", nextLabel: "Approve", nextStatus: "approved", color: "yellow" },
  { key: "approved", label: "Approved", nextLabel: "Process", nextStatus: "processing", color: "blue" },
  { key: "processing", label: "Processing", nextLabel: "Deliver", nextStatus: "delivered", color: "purple" },
  { key: "delivered", label: "Delivered", nextLabel: null, nextStatus: null, color: "green" },
];

const STATUS_BADGE = {
  requested: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

export default function ServiceRequests() {
  const [showModal, setShowModal] = useState(false);
  const [partsRequest, setPartsRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("requested");
  const [tabState, setTabState] = useState(
    Object.fromEntries(
      TABS.map((t) => [
        t.key,
        { requests: [], currentPage: 1, totalPages: 1, total: 0, isLoading: false, search: "" },
      ])
    )
  );
  const [selected, setSelected] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [printItems, setPrintItems] = useState(null);
  const [limit, setLimit] = useState(10);
  const printRef = useRef();

  const print = useReactToPrint({
    contentRef: printRef,
    documentTitle: "service-request",
  });

  const debouncedSearch = useDebounce(tabState[activeTab].search, 500);

  const setTab = useCallback((key, patch) => {
    setTabState((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  }, []);

  useEffect(() => {
    fetchData(activeTab);
    setSelected([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabState[activeTab].currentPage, debouncedSearch, updateTrigger, limit]);

  const fetchData = async (tab) => {
    const { currentPage, search } = tabState[tab];
    setTab(tab, { isLoading: true });
    try {
      const res = await privateAxios.get("/service-requests/", {
        params: { page: currentPage, limit, search, status: tab },
      });
      if (res.data.success) {
        setTab(tab, {
          requests: res.data.data,
          totalPages: res.data.totalPages,
          total: res.data.total,
          isLoading: false,
        });
      }
    } catch {
      toast.error("Failed to fetch service requests");
      setTab(tab, { isLoading: false });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request? This action cannot be undone.")) return;
    try {
      const res = await privateAxios.delete(`/service-requests/${id}`);
      if (res.data.success) {
        toast.success("Deleted successfully");
        setUpdateTrigger((n) => n + 1);
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleStatusChange = async (ids, nextLabel) => {
    const count = ids.length;
    const msg =
      count === 1
        ? `Mark this request as "${nextLabel}"?`
        : `Mark ${count} selected request(s) as "${nextLabel}"?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await privateAxios.patch("/service-requests/status", { ids });
      if (res.data.success) {
        toast.success(res.data.message);
        setSelected([]);
        setUpdateTrigger((n) => n + 1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  const handlePrintSelected = async () => {
    const { requests } = tabState[activeTab];
    const items = requests.filter((r) => selected.includes(r._id));
    if (items.length === 0) return;
    setPrintItems(items);
    await new Promise((r) => setTimeout(r, 80));
    print();
  };

  const handlePrintSingle = async (r) => {
    setPrintItems([r]);
    await new Promise((r) => setTimeout(r, 80));
    print();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const ids = tabState[activeTab].requests.map((r) => r._id);
    if (ids.every((id) => selected.includes(id))) {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const currentTab = TABS.find((t) => t.key === activeTab);
  const { requests, currentPage, totalPages, isLoading, search } = tabState[activeTab];
  const allCurrentSelected = requests.length > 0 && requests.every((r) => selected.includes(r._id));

  const goToPage = (p) => {
    const page = Math.max(1, Math.min(p, totalPages));
    setTab(activeTab, { currentPage: page });
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">
        {showModal && (
          <AddServiceRequest
            onClose={() => setShowModal(false)}
            onSuccess={() => setUpdateTrigger((n) => n + 1)}
          />
        )}

        {partsRequest && (
          <AddServiceParts
            request={partsRequest}
            onClose={() => setPartsRequest(null)}
            onSuccess={() => setUpdateTrigger((n) => n + 1)}
          />
        )}

        <div className="flex justify-between pt-5 pb-3 px-3">
          <span className="font-bold text-xl">Service Requests</span>
          <button
            onClick={() => setShowModal(true)}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            + New Request
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-0 -mb-px">
            {TABS.map((tab) => {
              const count = tabState[tab.key].total;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSelected([]);
                  }}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        isActive ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="flex flex-col gap-3">
          {/* Search + bulk actions */}
          <div className="flex justify-between items-center flex-wrap gap-2">
            <input
              type="text"
              placeholder="Search branch, receiver, service type..."
              value={search}
              onChange={(e) => {
                setTab(activeTab, { search: e.target.value, currentPage: 1 });
                setSelected([]);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-72 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />

            {selected.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{selected.length} selected</span>
                {currentTab.nextLabel && (
                  <button
                    onClick={() => handleStatusChange(selected, currentTab.nextStatus)}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    {currentTab.nextLabel} Selected
                  </button>
                )}
                <button
                  onClick={handlePrintSelected}
                  className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                >
                  Print Selected
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allCurrentSelected}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Branch</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Receiver</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Service Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Prepared By</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Requested By</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Parts Used</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Status Updated By</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={11} className="text-center py-8 text-gray-400">Loading...</td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-8 text-gray-400">No requests found</td>
                  </tr>
                ) : (
                  requests.map((r, idx) => (
                    <tr
                      key={r._id}
                      className={`hover:bg-gray-50 ${selected.includes(r._id) ? "bg-blue-50" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(r._id)}
                          onChange={() => toggleSelect(r._id)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-500">{(currentPage - 1) * 10 + idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.branchName}</td>
                      <td className="px-4 py-3 text-gray-700">{r.receiverName}</td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="text-xs">
                          <div className="font-medium">{r.serviceType}</div>
                          {r.description && (
                            <div className="text-gray-400 truncate max-w-[160px]" title={r.description}>
                              {r.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.preparedBy}</td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="text-xs">
                          <div className="font-medium">{r.requestedByName || "—"}</div>
                          {r.requestedAt && (
                            <div className="text-gray-400">{moment(r.requestedAt).format("DD/MM/YY HH:mm")}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.parts?.length > 0 ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {r.parts.map((p, i) => (
                              <li key={i} className="text-xs">
                                {p.productName} <span className="text-gray-400">(x{p.quantity})</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                        {moment(r.createdAt).format("DD/MM/YYYY")}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.statusUpdatedByName ? (
                          <div className="text-xs">
                            <div className="font-medium">{r.statusUpdatedByName}</div>
                            {r.statusUpdatedAt && (
                              <div className="text-gray-400">
                                {moment(r.statusUpdatedAt).format("DD/MM/YY HH:mm")}
                              </div>
                            )}
                            <span className={`inline-flex mt-0.5 items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}>
                              {r.status}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          {currentTab.nextLabel && (
                            <button
                              onClick={() => handleStatusChange([r._id], currentTab.nextStatus)}
                              className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-2.5 py-0.5 rounded text-xs"
                            >
                              {currentTab.nextLabel}
                            </button>
                          )}
                          {r.status === "processing" && (
                            <button
                              onClick={() => setPartsRequest(r)}
                              className="bg-purple-500 hover:bg-purple-600 text-white font-medium px-2.5 py-0.5 rounded text-xs"
                            >
                              Add Parts
                            </button>
                          )}
                          <button
                            onClick={() => handlePrintSingle(r)}
                            className="bg-green-500 hover:bg-green-600 text-white font-medium px-2.5 py-0.5 rounded text-xs"
                          >
                            Print
                          </button>
                          <button
                            onClick={() => handleDelete(r._id)}
                            className="bg-red-500 hover:bg-red-600 text-white font-medium px-2.5 py-0.5 rounded text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex justify-end items-center gap-2 pb-4 flex-wrap">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>

              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page;
                if (totalPages <= 7) {
                  page = i + 1;
                } else if (currentPage <= 4) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  page = totalPages - 6 + i;
                } else {
                  page = currentPage - 3 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? "bg-blue-600 text-white border-blue-600"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>

              <span className="text-sm text-gray-500 ml-1">of {totalPages}</span>

              {/* Per-page selector */}
              <div className="flex items-center gap-1 ml-2">
                <span className="text-sm text-gray-500">Per page:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setTab(activeTab, { currentPage: 1 });
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden print area */}
      {printItems && (
        <div className="absolute opacity-0 pointer-events-none">
          <ServiceRequestInvoice ref={printRef} items={printItems} />
        </div>
      )}
    </div>
  );
}
